import groupBy from 'lodash/groupBy';
import {
	SERVICE_REQUEST,
	CHANGE_MANAGEMENT,
	PROBLEM_MANAGEMENT,
	INCIDENT_MANAGEMENT,
	POST_INCIDENT_REVIEW,
	DEVELOPER_ESCALATION,
	ISSUE_TYPE,
	REQUEST_TYPE,
	UNASSIGNED_REQUEST_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import type {
	IssueType,
	RequestType,
	RequestTypePractices,
	IssueOrRequestType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import messages from './messages';
import type {
	RequestTypeOption,
	RequestAndIssueTypeOptions,
	NoneOption,
} from './request-type-select/types';

export const getFieldLabel = (hasRequestTypes: boolean, formatMessage: FormatMessage): string =>
	hasRequestTypes
		? formatMessage(messages.typeFieldLabel)
		: formatMessage(messages.issueTypeFieldLabel);

export const getRequestTypeGroupLabel = (
	practice: RequestTypePractices,
	formatMessage: FormatMessage,
): string => {
	if (practice === CHANGE_MANAGEMENT) {
		return formatMessage(messages.changeRequestGroupLabel);
	}
	if (practice === INCIDENT_MANAGEMENT) {
		return formatMessage(messages.incidentRequestGroupLabel);
	}
	if (practice === PROBLEM_MANAGEMENT) {
		return formatMessage(messages.problemRequestGroupLabel);
	}
	if (practice === SERVICE_REQUEST) {
		return formatMessage(messages.serviceRequestGroupLabel);
	}
	if (practice === POST_INCIDENT_REVIEW) {
		return formatMessage(messages.postIncidentReviewRequestGroupLabel);
	}
	if (practice === DEVELOPER_ESCALATION) {
		return formatMessage(messages.developerEscalationRequestGroupLabel);
	}
	if (practice === UNASSIGNED_REQUEST_TYPE) {
		return formatMessage(messages.unassignedRequestTypeGroupLabel);
	}

	return formatMessage(messages.requestTypeGroupLabel);
};

const reorderRequestTypeGroups = (
	requestTypeGroupKeys: RequestTypePractices[],
): RequestTypePractices[] => {
	// This is the order we have them for queues, request types and feature page
	const allPractices: RequestTypePractices[] = [
		SERVICE_REQUEST,
		INCIDENT_MANAGEMENT,
		PROBLEM_MANAGEMENT,
		CHANGE_MANAGEMENT,
		POST_INCIDENT_REVIEW,
		DEVELOPER_ESCALATION,
		UNASSIGNED_REQUEST_TYPE,
		REQUEST_TYPE,
	];
	const groupKeySet = new Set(requestTypeGroupKeys);
	return allPractices.filter((practice) => groupKeySet.has(practice));
};

const createNoneOption = (formatMessage: FormatMessage): NoneOption => ({
	label: formatMessage(messages.noRequestTypeLabel),
	value: 'none',
	type: 'NONE',
});

export const getRequestAndIssueTypeOptionsGroup = (
	issueTypes: IssueType[],
	requestTypes: RequestType[],
	formatMessage: FormatMessage,
): RequestAndIssueTypeOptions => {
	// @ts-expect-error(PARTIAL_RECORD) TS2322 | TS2322 - Type 'Dictionary<RequestType[]>' is not assignable to type 'Record<RequestTypePractices, RequestType[]>'. | Type 'Dictionary<RequestType[]>' is not assignable to type 'Record<RequestTypePractices, RequestType[]>'.
	const groupedRequestTypes: Record<RequestTypePractices, RequestType[]> = groupBy(
		requestTypes,
		(o) => o.practice,
	);

	// @ts-expect-error - TS2345 - Argument of type 'string[]' is not assignable to parameter of type 'RequestTypePractices[]'.
	const groupKeys = reorderRequestTypeGroups(Object.keys(groupedRequestTypes));
	const requestTypesOptions: RequestTypeOption[] = groupKeys.map((practice) => ({
		label: getRequestTypeGroupLabel(practice, formatMessage),
		options: groupedRequestTypes[practice],
	}));

	return {
		issueTypeOptions: issueTypes,
		requestTypeOptions: [createNoneOption(formatMessage), ...requestTypesOptions],
	};
};

export const getIssueTypeAndRequestType = ({
	requestOrIssueType,
	allIssueTypes,
	formatMessage,
}: {
	requestOrIssueType: IssueOrRequestType;
	allIssueTypes: IssueType[];
	formatMessage: FormatMessage;
}): {
	issueType: IssueType | null;
	requestType: RequestType | NoneOption | null;
} => {
	const noneOption = createNoneOption(formatMessage);

	let issueType: IssueType | null = null;
	let requestType: RequestType | NoneOption | null = null;

	if (requestOrIssueType !== null) {
		switch (requestOrIssueType.type) {
			case ISSUE_TYPE:
				issueType = requestOrIssueType;
				requestType = noneOption;
				break;
			case REQUEST_TYPE:
				issueType =
					allIssueTypes.find(({ value }) => value === requestOrIssueType.issueTypeId) || null;
				requestType = requestOrIssueType;
				break;
			default: // default case forced by flow
		}
	}

	return { issueType, requestType };
};

export const getFirstMatchingRequestType = ({
	issueType,
	allRequestTypes,
}: {
	issueType: IssueType;
	allRequestTypes: RequestType[];
}): RequestType | undefined =>
	allRequestTypes.find(({ issueTypeId }) => issueTypeId === issueType.value);
